import React, { useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useParams } from "react-router";
import { COMMUNITY_TYPE_TO_TEXT } from "@sscs/report/constants";
import { Card, Menu, QueryResult, Select } from "@sscs/ui";
import { getQueryStatus } from "@sscs/utils";
import useSearch from "../../../hooks/useSearch";
import { useGetIssueDetailsQuery } from "../../../services/insights.api";
import { Insight, InsightsTimeline } from "../../../types";
import DateChart from "./DateChart";
import styles from "./insights.module.scss";
import SbomChart from "./SbomChart";

const VERSION_DATA_SOURCE: Record<string, string> = {
  upload: "Upload",
  release: "Release",
  approval: "Approval",
};

type VersionDataSource = keyof typeof VERSION_DATA_SOURCE;

const NO_VERSION_DATA: Record<VersionDataSource, string> = {
  upload: "No uploaded versions", // should never happen
  release: "No released versions",
  approval: "No approved versions",
};

type InsightsProps = {
  issue: Insight;
};

const Insights = ({ issue }: InsightsProps): React.JSX.Element | null => {
  const { groupId } = useParams();
  const { filters, isSearchFetching } = useSearch();
  const {
    data: issueDetails,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetIssueDetailsQuery(
    groupId && !isSearchFetching
      ? {
          groupId,
          filters,
          id: issue.id,
          type: issue.issue_type,
        }
      : skipToken,
  );
  const [versionDataSource, setVersionDataSource] =
    useState<VersionDataSource>("upload");
  let versionTimeline: InsightsTimeline | undefined = undefined;

  if (versionDataSource === "upload") {
    versionTimeline = issueDetails?.timelines.version_uploaded ?? {};
  } else if (versionDataSource === "release") {
    versionTimeline = issueDetails?.timelines.version_released ?? {};
  } else if (versionDataSource === "approval") {
    versionTimeline = issueDetails?.timelines.version_approved ?? {};
  }

  return (
    <QueryResult
      status={getQueryStatus({
        isError,
        isFetching,
        isLoading,
        error,
        data: issueDetails,
      })}
    >
      <div className={styles["insights-wrapper"]}>
        <Card.Card>
          <Card.Title
            actions={
              <Select
                value={versionDataSource}
                onChange={(value) =>
                  setVersionDataSource(value as VersionDataSource)
                }
                shrinkLabel
                id="select-date-grouping"
                labelId="date-grouping-label"
                label="Group versions by"
                className={styles["group-by-dropdown"]}
              >
                {Object.keys(VERSION_DATA_SOURCE).map((item) => (
                  <Menu.Item key={item} value={item}>
                    {VERSION_DATA_SOURCE[item]} Date
                  </Menu.Item>
                ))}
              </Select>
            }
          >
            Affected versions
          </Card.Title>
          <Card.Content>
            {versionTimeline && Object.keys(versionTimeline).length > 0 ? (
              <DateChart timeline={versionTimeline} />
            ) : (
              <div className={styles["date-chart-no-data"]}>
                {NO_VERSION_DATA[versionDataSource]}
              </div>
            )}
          </Card.Content>
        </Card.Card>
        <Card.Card>
          <Card.Title>Affected components by age</Card.Title>
          <Card.Content>
            {issueDetails?.timelines.component_age &&
            Object.keys(issueDetails.timelines.component_age).length > 0 ? (
              <DateChart timeline={issueDetails.timelines.component_age} />
            ) : (
              <div className={styles["date-chart-no-data"]}>
                Age of affected components not known
              </div>
            )}
          </Card.Content>
        </Card.Card>
        <div className={styles["sbom-insights"]}>
          <Card.Card>
            <Card.Title>Top 10 affected publishers</Card.Title>
            <Card.Content>
              <SbomChart
                sbomInsights={issueDetails?.sbom.top_5_publishers ?? []}
              />
            </Card.Content>
          </Card.Card>
          <Card.Card>
            <Card.Title>Top 10 affected communities</Card.Title>
            <Card.Content>
              <SbomChart
                sbomInsights={(issueDetails?.sbom.top_5_communities ?? []).map(
                  (community) => ({
                    ...community,
                    label: COMMUNITY_TYPE_TO_TEXT[community.label],
                  }),
                )}
              />
            </Card.Content>
          </Card.Card>
        </div>
      </div>
    </QueryResult>
  );
};

export default Insights;
